import { useMemo, useRef, useState, type CSSProperties } from "react";
import { Select } from "antd";
import { UserOutlined } from "@ant-design/icons";
import {
  findResponsibilityName,
  findResponsibilityOption,
  type AddDesignScopeResponsibility,
  type DesignScopeResponsibilityOption
} from "../shared/design-scope-responsibilities";
import { RESPONSIBILITIES } from "./scope-item-field";

const ADD_HINT =
  "For adding new responsible person write any name and click on add.";

/**
 * Responsibility picker.
 * With `catalog`, options come from the project list (A–Z) and a new name is
 * saved with `onAdd`. Without `catalog`, the built-in list still accepts
 * free-form text on the row only.
 */
export default function ScopeResponsibilitySelect(props: {
  value: string;
  onChange: (value: string) => void;
  /** Project catalog. Option values are ids; labels are names. */
  catalog?: DesignScopeResponsibilityOption[];
  catalogLoading?: boolean;
  onAdd?: AddDesignScopeResponsibility;
  testId: string;
  className?: string;
  style?: CSSProperties;
}) {
  const {
    value,
    onChange,
    catalog,
    catalogLoading,
    onAdd,
    testId,
    className,
    style
  } = props;
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState<DesignScopeResponsibilityOption | null>(
    null
  );
  const addingRef = useRef(false);

  const pendingAdd = useMemo(() => {
    if (!catalog) return "";
    const typed = query.trim();
    const names = catalog.map((option) => option.name);
    if (added) names.push(added.name);
    if (!typed || findResponsibilityName(names, typed)) return "";
    return typed;
  }, [added, catalog, query]);

  const options = useMemo(() => {
    if (!catalog) {
      const presets = RESPONSIBILITIES.map((name) => ({
        value: name,
        label: name
      }));
      const extras: string[] = [];
      if (value && !RESPONSIBILITIES.includes(value)) extras.push(value);
      const typed = query.trim();
      if (
        typed &&
        !RESPONSIBILITIES.includes(typed) &&
        !extras.includes(typed)
      ) {
        extras.push(typed);
      }
      return [
        ...extras.map((name) => ({ value: name, label: name })),
        ...presets
      ];
    }
    const rows = catalog.map((option) => ({
      value: option.id,
      label: option.name
    }));
    const seen = new Set(rows.map((row) => row.value));
    const pushExtra = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      rows.push({ value: id, label });
    };
    if (added) pushExtra(added.id, added.name);
    if (value && !seen.has(value)) pushExtra(value, value);
    rows.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
    return rows;
  }, [added, catalog, query, value]);

  const confirmNewName = async (raw: string) => {
    const typed = raw.trim();
    if (!typed || addingRef.current) return;
    const existing = catalog
      ? findResponsibilityOption(catalog, typed)
      : undefined;
    if (existing) {
      onChange(existing.id);
      setQuery("");
      return;
    }
    if (added && findResponsibilityName([added.name], typed) && added.id) {
      onChange(added.id);
      setQuery("");
      return;
    }
    if (!onAdd) return;
    addingRef.current = true;
    setAdding(true);
    try {
      const result = await onAdd(typed);
      if (!result.success || !result.id) return;
      setAdded({ id: result.id, name: result.name || typed });
      onChange(result.id);
      setQuery("");
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  return (
    <Select
      allowClear
      virtual={false}
      className={className}
      style={style}
      data-testid={testId}
      placeholder="Select or type a responsibility"
      suffixIcon={<UserOutlined />}
      loading={Boolean(catalogLoading) || adding}
      value={value || undefined}
      options={options}
      // An empty filtered list refuses to open unless notFoundContent is set.
      open={catalog && pendingAdd ? true : undefined}
      notFoundContent={pendingAdd ? <span /> : undefined}
      popupRender={
        catalog
          ? (menu) => (
              <div className="scope-responsibility-popup">
                {pendingAdd && onAdd ? (
                  <div className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 truncate text-sm text-black/88">
                      {pendingAdd}
                    </span>
                    <button
                      type="button"
                      className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm text-ciq-primary-800"
                      data-testid={`${testId}-add`}
                      disabled={adding}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => void confirmNewName(pendingAdd)}
                    >
                      + Add
                    </button>
                  </div>
                ) : (
                  <div className="px-3 pb-1 pt-2 text-xs text-black/45">
                    {ADD_HINT}
                  </div>
                )}
                {menu}
              </div>
            )
          : undefined
      }
      showSearch={{
        searchValue: query,
        onSearch: setQuery,
        filterOption: (input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(String(input ?? "").toLowerCase())
      }}
      onChange={(next) => {
        onChange(next ?? "");
        setQuery("");
      }}
      onInputKeyDown={(event) => {
        if (event.key !== "Enter") return;
        const typed = query.trim();
        if (!typed) return;
        if (!catalog) {
          event.preventDefault();
          onChange(typed);
          setQuery("");
          return;
        }
        const existing = findResponsibilityOption(catalog, typed);
        if (existing) {
          event.preventDefault();
          onChange(existing.id);
          setQuery("");
          return;
        }
        const names = [
          ...catalog.map((option) => option.name),
          ...(added ? [added.name] : [])
        ];
        const matchesCatalog = names.some((name) =>
          name.toLowerCase().includes(typed.toLowerCase())
        );
        if (matchesCatalog) return;
        event.preventDefault();
        void confirmNewName(typed);
      }}
      onBlur={() => {
        if (!catalog) {
          const typed = query.trim();
          if (typed && typed !== value) onChange(typed);
        }
        setQuery("");
      }}
    />
  );
}
